import type { AppConfig } from '../config';

/**
 * Authorization Code flow with PKCE against the Cognito Hosted UI.
 *
 * Passwords, MFA enrolment, the forced reset on first sign-in and account
 * recovery are all handled by Cognito's hosted pages — this app never sees a
 * credential, which removes an entire category of risk from the CMS.
 */

const VERIFIER_KEY = 'cms.pkce.verifier';
const STATE_KEY = 'cms.pkce.state';
const TOKENS_KEY = 'cms.tokens';

export interface Tokens {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  /** Epoch milliseconds. */
  expiresAt: number;
}

const redirectUri = () => `${window.location.origin}/callback`;

function base64Url(bytes: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function randomString(byteLength = 48): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64Url(bytes.buffer);
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(digest);
}

/**
 * Start the hosted flow at `endpoint`.
 *
 * `/forgotPassword` takes the same parameters as `/oauth2/authorize`, so a reset
 * carries the PKCE challenge through and finishes by landing on `/callback`
 * already signed in — rather than dumping the user back at a login screen.
 */
async function beginHostedFlow(
  config: AppConfig,
  endpoint: 'oauth2/authorize' | 'forgotPassword',
  returnTo: string,
): Promise<void> {
  const verifier = randomString();
  // `state` carries the post-login destination and is verified on return, so a
  // forged callback cannot inject an authorization code.
  const state = `${randomString(16)}.${btoa(returnTo)}`;

  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.userPoolClientId,
    redirect_uri: redirectUri(),
    scope: 'openid email profile',
    state,
    code_challenge: await challengeFor(verifier),
    code_challenge_method: 'S256',
  });
  window.location.assign(`${config.cognitoDomain}/${endpoint}?${params}`);
}

export async function beginLogin(config: AppConfig, returnTo = '/'): Promise<void> {
  return beginHostedFlow(config, 'oauth2/authorize', returnTo);
}

/**
 * Jump straight to the reset form. Without this the only way to reach it is to
 * press "Sign in" and then spot Cognito's own "Forgot your password?" link —
 * which reads as "the CMS has no password reset".
 */
export async function beginPasswordReset(config: AppConfig, returnTo = '/'): Promise<void> {
  return beginHostedFlow(config, 'forgotPassword', returnTo);
}

export async function completeLogin(
  config: AppConfig,
  code: string,
  state: string,
): Promise<{ tokens: Tokens; returnTo: string }> {
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);

  if (!expectedState || state !== expectedState) throw new Error('Sign-in state mismatch');
  if (!verifier) throw new Error('Sign-in verifier missing — please try again');

  const tokens = await exchange(config, {
    grant_type: 'authorization_code',
    client_id: config.userPoolClientId,
    code,
    redirect_uri: redirectUri(),
    code_verifier: verifier,
  });

  let returnTo = '/';
  try {
    returnTo = atob(expectedState.split('.')[1] ?? '') || '/';
  } catch {
    returnTo = '/';
  }
  // Never bounce to an absolute URL supplied through state.
  if (!returnTo.startsWith('/')) returnTo = '/';

  storeTokens(tokens);
  return { tokens, returnTo };
}

export async function refresh(config: AppConfig, refreshToken: string): Promise<Tokens> {
  const tokens = await exchange(config, {
    grant_type: 'refresh_token',
    client_id: config.userPoolClientId,
    refresh_token: refreshToken,
  });
  // Cognito does not return a new refresh token on this grant.
  const merged = { ...tokens, refreshToken };
  storeTokens(merged);
  return merged;
}

async function exchange(config: AppConfig, body: Record<string, string>): Promise<Tokens> {
  const res = await fetch(`${config.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(body),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`Token exchange failed (${res.status}): ${detail.slice(0, 200)}`);
  }
  const json = (await res.json()) as {
    id_token: string;
    access_token: string;
    refresh_token?: string;
    expires_in: number;
  };
  return {
    idToken: json.id_token,
    accessToken: json.access_token,
    refreshToken: json.refresh_token,
    expiresAt: Date.now() + json.expires_in * 1000,
  };
}

/**
 * sessionStorage rather than localStorage: tokens die with the tab, so a shared
 * or unattended machine does not leave a signed-in CMS behind.
 */
export function storeTokens(tokens: Tokens): void {
  sessionStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
}

export function readTokens(): Tokens | undefined {
  const raw = sessionStorage.getItem(TOKENS_KEY);
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as Tokens;
  } catch {
    return undefined;
  }
}

export function clearTokens(): void {
  sessionStorage.removeItem(TOKENS_KEY);
}

export function logout(config: AppConfig): void {
  clearTokens();
  const params = new URLSearchParams({
    client_id: config.userPoolClientId,
    logout_uri: window.location.origin,
  });
  window.location.assign(`${config.cognitoDomain}/logout?${params}`);
}

export interface IdTokenClaims {
  sub: string;
  email?: string;
  name?: string;
  'cognito:groups'?: string[];
  exp: number;
}

/**
 * Decodes without verifying — purely to drive the UI. Every real authorization
 * decision is made server-side by the API Gateway JWT authorizer.
 */
export function decodeIdToken(idToken: string): IdTokenClaims | undefined {
  try {
    const payload = idToken.split('.')[1];
    if (!payload) return undefined;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodeURIComponent(escape(json))) as IdTokenClaims;
  } catch {
    return undefined;
  }
}
